/**
 * What the signed-in member may see or do in a workspace, from the server's decisions in the organization context.
 *
 * On the capability policy the `permissions` list is authoritative: no role name grants anything on its own, because
 * an Admin, for example, cannot archive or approve payouts there. Legacy workspaces predate capabilities, so their
 * permission lists don't name every newer feature; Owner, Admin and platform support keep their broad access there.
 * The UI only hides what the server would refuse; every action is still authorized by the API.
 */
export type WorkspaceAccessSubject = {
  accessScopeType?: string | null;
  authorizationScopeType?: string | null;
  owner?: boolean | null;
  orgRole?: string | null;
  permissions?: readonly string[] | null;
  authorizationPolicyVersion?: string | null;
};

const LEGACY_BROAD_ROLES = new Set(['OWNER', 'ADMIN', 'PLATFORM_SUPPORT']);

export function onCapabilityPolicy(workspace: WorkspaceAccessSubject | null | undefined): boolean {
  const version = String(workspace?.authorizationPolicyVersion ?? 'legacy').trim().toLowerCase();
  return version !== '' && version !== 'legacy';
}

/** Owner, Admin or platform support in a legacy workspace; never true on the capability policy. */
export function legacyBroadAccess(workspace: WorkspaceAccessSubject | null | undefined): boolean {
  if (!workspace || onCapabilityPolicy(workspace)) return false;
  return Boolean(workspace.owner) || LEGACY_BROAD_ROLES.has(normalize(workspace.orgRole));
}

export function hasWorkspacePermission(
  workspace: WorkspaceAccessSubject | null | undefined,
  ...permissions: string[]
): boolean {
  if (!workspace) return false;
  if (legacyBroadAccess(workspace)) return true;
  const granted = new Set((workspace.permissions ?? []).map(normalize));
  return permissions.some((permission) => {
    const requested = normalize(permission);
    if (onCapabilityPolicy(workspace)) return granted.has(requested);
    if (requested === 'ACCOUNTING_READ') {
      return workspace.accessScopeType !== 'ASSIGNED_PROPERTIES_ONLY'
        && granted.has('PAYMENT_VIEW') && granted.has('REPORT_VIEW');
    }
    const legacy: Record<string, string[]> = {
      APPLICATION_VIEW: ['TENANT_VIEW', 'LEASE_VIEW'], INSPECTION_VIEW: ['MAINTENANCE_VIEW'],
      // Same as the server on legacy workspaces: inspections use the maintenance permissions, and finishing one
      // only needs update access (the separate finalize permission applies on the capability policy).
      INSPECTION_CREATE: ['MAINTENANCE_CREATE'], INSPECTION_UPDATE: ['MAINTENANCE_UPDATE'], INSPECTION_FINALIZE: ['MAINTENANCE_UPDATE'],
      CALENDAR_VIEW: ['LEASE_VIEW', 'MAINTENANCE_VIEW'], COMMUNICATION_VIEW: ['ORG_VIEW'],
    };
    return granted.has(requested) || (legacy[requested] ?? []).some(value => granted.has(value));
  });
}

/** Role-named access (older route metadata). On the capability policy roles alone grant nothing. */
export function hasWorkspaceRole(workspace: WorkspaceAccessSubject | null | undefined, roles: readonly string[]): boolean {
  if (!workspace) return false;
  if (legacyBroadAccess(workspace)) return true;
  if (onCapabilityPolicy(workspace)) return false;
  const role = normalize(workspace.orgRole);
  return roles.some((allowed) => normalize(allowed) === role);
}

/**
 * "Owner only" screens (team, billing, workspace settings). Legacy: Owner/Admin. Capability policy: the given
 * permissions decide, falling back to workspace settings management.
 */
export function ownerOnlyAllowed(
  workspace: WorkspaceAccessSubject | null | undefined,
  permissions: readonly string[] = [],
): boolean {
  if (!workspace) return false;
  if (!onCapabilityPolicy(workspace)) return legacyBroadAccess(workspace);
  return hasWorkspacePermission(workspace, ...(permissions.length ? permissions : ['ORG_EDIT_SETTINGS']));
}

const ORGANIZATION_WIDE_BY_DEFAULT = new Set(['OWNER', 'ADMIN', 'ACCOUNTANT', 'BOOKKEEPER']);

/**
 * The scope a new team member starts with (role matrix): finance roles and admins see the whole workspace; everyone
 * else only the properties they are assigned, chosen explicitly. Matches the server default for invitations.
 */
export function defaultInviteScope(role: string | null | undefined): 'ORGANIZATION_WIDE' | 'ASSIGNED_PROPERTIES_ONLY' {
  return ORGANIZATION_WIDE_BY_DEFAULT.has(normalize(role)) ? 'ORGANIZATION_WIDE' : 'ASSIGNED_PROPERTIES_ONLY';
}

function normalize(value: string | null | undefined): string {
  return String(value ?? '').trim().toUpperCase();
}

/** Aggregate backoffice calendar; portal schedules enforce their own record participation. */
export function workspaceCalendarAllowed(workspace: WorkspaceAccessSubject | null | undefined): boolean {
  if (!hasWorkspacePermission(workspace, 'CALENDAR_VIEW')) return false;
  const scope = normalize(onCapabilityPolicy(workspace) ? workspace?.authorizationScopeType : workspace?.accessScopeType);
  if (onCapabilityPolicy(workspace)) return scope === 'ORGANIZATION_WIDE' || scope === 'ASSIGNED_PROPERTIES_ONLY';
  if (scope && scope !== 'ORGANIZATION_WIDE' && scope !== 'ASSIGNED_PROPERTIES_ONLY') return false;
  return !['TENANT', 'TENANT_PORTAL', 'LANDLORD_PORTAL', 'MAINTENANCE', 'MAINTENANCE_STAFF', 'FIELD_INSPECTOR']
    .includes(normalize(workspace?.orgRole));
}
